import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  signal,
  input,
  inject,
} from '@angular/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotationsService } from '@pages/quotations';
import { switchMap } from 'rxjs';

import {
  VOneButtonComponent,
  VOneDropdownButtonComponent,
} from '@shared-components';
import { CardState, DropdownItem, DropdownOption } from '@shared-models';
import { Note } from '@shared-models';
import { getRelatedNotes$, isPortfolioCardState } from '@shared-utils';

@Component({
  selector: 'v-one-card-footer',
  imports: [
    forwardRef(() => VOneDropdownButtonComponent),
    forwardRef(() => VOneButtonComponent),
    CurrencyPipe,
    TranslateModule,
  ],
  templateUrl: './card-footer.component.html',
  styleUrl: './card-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardFooterComponent {
  @Input() showDropdown = true;
  @Input() notes?: string;
  @Input() notesCount?: number;
  @Input() cardState: CardState = 'order';
  @Input() currency = 'USD';
  @Input() hasAdvance = false;
  @Input() advanceAmount?: number;
  @Input() hasFavorBalance = false;
  @Input() favorBalanceAmount?: number;
  @Input() set dropdownItems(items: DropdownItem[]) {
    this.dropdownOptions.set(
      items.map((item) => ({
        label: item.label,
        value: item.value,
        icon: item.icon,
      })),
    );
  }

  // Overrides the footer CTA. With no dropdown items, ctaAction is emitted
  // directly on click instead of opening a dropdown.
  @Input() ctaLabel?: string;
  @Input() ctaAction?: string;

  @Output() dropdownAction = new EventEmitter<string>();

  readonly idInvoice = input<number>();
  private readonly translate = inject(TranslateService);
  private quotationsService = inject(QuotationsService);
  protected dropdownOptions = signal<DropdownOption[]>([]);

  protected readonly advanceSelected = signal(false);
  protected readonly favorBalanceSelected = signal(false);

  relatedNotes = toSignal(
    toObservable(this.idInvoice).pipe(
      switchMap((id) => getRelatedNotes$(this.quotationsService, id)),
    ),
    { initialValue: [] as Note[] },
  );

  protected isPortfolioCard(): boolean {
    return isPortfolioCardState(this.cardState);
  }

  // Anticipo/saldo a favor selection only applies while the invoice still
  // has a balance to settle — once paid/voided there's nothing left to apply.
  protected readonly showPaymentOptions = computed(() => {
    const pendingStates: CardState[] = ['invoice-unpaid', 'invoice-partial'];
    return (
      this.isPortfolioCard() &&
      pendingStates.includes(this.cardState) &&
      (this.hasAdvance || this.hasFavorBalance)
    );
  });

  protected onAdvanceToggle(event: Event): void {
    this.toggleAdvance((event.target as HTMLInputElement).checked);
  }

  protected onFavorBalanceToggle(event: Event): void {
    this.toggleFavorBalance((event.target as HTMLInputElement).checked);
  }

  protected toggleAdvance(selected: boolean): void {
    this.advanceSelected.set(selected);
    this.dropdownAction.emit(selected ? 'apply-advance' : 'remove-advance');
  }

  protected toggleFavorBalance(selected: boolean): void {
    this.favorBalanceSelected.set(selected);
    this.dropdownAction.emit(
      selected ? 'apply-favor-balance' : 'remove-favor-balance',
    );
  }

  protected handleDropdownSelect(
    option: DropdownOption | DropdownOption[],
  ): void {
    if (!Array.isArray(option)) {
      this.dropdownAction.emit(option.value);
    }
  }

  protected handleDirectAction(): void {
    if (this.ctaAction) {
      this.dropdownAction.emit(this.ctaAction);
    }
  }
}
